import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import api from '../services/api';
import toast from 'react-hot-toast';
import { RefreshCw, ArrowDown, Wallet, ArrowRightLeft } from 'lucide-react';

const ConvertPage = () => {
    const [tokens, setTokens] = useState([]);
    const [fromToken, setFromToken] = useState(null);
    const [toToken, setToToken] = useState(null);
    const [amount, setAmount] = useState('');
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchTokens();
    }, []);

    const fetchTokens = async () => {
        try {
            const res = await api.get('/wallet/tokens');
            const supported = res.data.tokens || [];
            setTokens(supported);
            if (supported.length > 0) {
                setFromToken(supported.find(t => t.symbol === 'USDT') || supported[0]);
                setToToken(supported.find(t => t.symbol === 'BTC') || supported[1]);
            }
        } catch (error) {
            console.error('Failed to tokens', error);
        }
    };

    const handleSwap = () => {
        const temp = fromToken;
        setFromToken(toToken);
        setToToken(temp);
        setQuote(null);
    };

    const getQuote = async () => {
        if (!amount || !fromToken || !toToken) return;
        setLoading(true);
        // Simulate API quote
        setTimeout(() => {
            const price = fromToken.symbol === 'USDT' ? (1 / 45000) : 45000; // Mock price
            setQuote((parseFloat(amount) * price).toFixed(6));
            setLoading(false);
        }, 1000);
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (parseFloat(amount) > 0) getQuote();
        }, 800);
        return () => clearTimeout(timeout);
    }, [amount, fromToken, toToken]);

    return (
        <div className="bg-[#0b0e11] min-h-screen text-white font-sans selection:bg-blue-500/30">
            <Navbar />

            <div className="max-w-xl mx-auto px-4 py-20">
                <div className="text-center mb-10">
                    <h1 className="text-4xl font-bold mb-4">Convert & Swap</h1>
                    <p className="text-gray-400">Instantly swap tokens with zero fees.</p>
                </div>

                <div className="bg-[#181a20] rounded-3xl p-6 border border-gray-800 shadow-2xl relative">

                    {/* From */}
                    <div className="bg-[#0b0e11] rounded-2xl p-4 border border-gray-700/50 hover:border-gray-600 transition">
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-400 text-sm font-medium">From</span>
                            <span className="text-gray-400 text-sm flex items-center gap-1">
                                <Wallet size={14} /> Balance: 0.00
                            </span>
                        </div>
                        <div className="flex items-center gap-4">
                            <input
                                type="number"
                                placeholder="0.00"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                className="bg-transparent text-3xl font-bold w-full outline-none placeholder-gray-600"
                            />
                            <select
                                value={fromToken?.id}
                                onChange={(e) => setFromToken(tokens.find(t => String(t.id) === e.target.value))}
                                className="bg-[#2b3139] px-3 py-2 rounded-xl border border-gray-700 font-bold outline-none"
                            >
                                {tokens.map(t => <option key={t.id} value={t.id}>{t.symbol}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Swap Icon */}
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                        <button
                            onClick={handleSwap}
                            className="bg-[#181a20] border-4 border-[#0b0e11] p-3 rounded-xl hover:bg-gray-800 transition shadow-lg"
                        >
                            <ArrowDown size={20} className="text-blue-500" />
                        </button>
                    </div>

                    {/* To */}
                    <div className="bg-[#0b0e11] rounded-2xl p-4 border border-gray-700/50 mt-4 hover:border-gray-600 transition">
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-400 text-sm font-medium">To (Estimated)</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <input
                                type="text"
                                readOnly
                                value={loading ? 'Loading...' : quote || ''}
                                placeholder="0.00"
                                className="bg-transparent text-3xl font-bold w-full outline-none placeholder-gray-600 text-gray-300"
                            />
                            <select
                                value={toToken?.id}
                                onChange={(e) => setToToken(tokens.find(t => String(t.id) === e.target.value))}
                                className="bg-[#2b3139] px-3 py-2 rounded-xl border border-gray-700 font-bold outline-none"
                            >
                                {tokens.map(t => <option key={t.id} value={t.id}>{t.symbol}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Info */}
                    <div className="mt-6 space-y-3">
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Rate</span>
                            <span className="text-white font-medium">1 {fromToken?.symbol} ≈ {loading ? '...' : (quote / amount || 0).toFixed(6)} {toToken?.symbol}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Fees</span>
                            <span className="text-green-500 font-medium">No Fees</span>
                        </div>
                    </div>

                    <button
                        disabled={!quote || loading}
                        className="w-full mt-8 bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 rounded-xl text-lg shadow-lg shadow-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                        {loading ? 'Fetching Quote...' : 'Preview Conversion'}
                    </button>

                </div>
            </div>
        </div>
    );
};

export default ConvertPage;
